import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import React, { useEffect, useRef } from "react";
import type {
    CalloutAlignment,
    CalloutArrowPosition,
} from "../community-search/search-bar/use-dynamic-callout-position";

interface CalloutChildrenParams {
    close: () => void;
}

interface CalloutProps {
    renderButton: () => JSX.Element;
    onOpen?: () => void;
    forceClose?: boolean;
    children: React.ReactNode | ((params: CalloutChildrenParams) => React.ReactNode);
    align?: CalloutAlignment;
    arrowPosition?: CalloutArrowPosition;
    noPadding?: boolean;
}

const PANEL_ALIGNMENT_CLASSES: Record<CalloutAlignment, string> = {
    left: "",
    right: "right-0",
    "xs-right": "right-0 sm:left-0 sm:right-auto",
};

const ARROW_POSITION_CLASSES: Record<CalloutArrowPosition, string> = {
    left: "ml-1 left-4",
    right: "mr-1 right-4",
    center: "left-1/2 -ml-2",
    "xs-center": "left-1/2 -ml-2 sm:left-auto sm:right-auto sm:ml-1",
};

export const Callout: React.FC<CalloutProps> = props => {
    const {
        onOpen,
        forceClose,
        children,
        renderButton,
        align = "left",
        arrowPosition,
        noPadding = false,
    } = props;
    const isOpenRef = useRef(false);

    const effectiveArrowPosition =
        arrowPosition ||
        (align === "right" ? "right" : align === "xs-right" ? "right" : "left");
    const theButton = useRef<HTMLButtonElement>();
    useEffect(() => {
        if (forceClose && isOpenRef.current && theButton.current) {
            theButton.current.click();
        }
    }, [forceClose]);
    return (
        <Popover className="relative">
            {({ open, close }) => {
                isOpenRef.current = open;
                return (
                    <>
                        <PopoverButton ref={theButton} as="div" onClick={onOpen}>
                            {renderButton()}
                        </PopoverButton>
                        <PopoverPanel
                            className={`absolute z-20 mt-4 ${PANEL_ALIGNMENT_CLASSES[align]}`}>
                            <div
                                className={`relative bg-white shadow-xl sm:mx-auto sm:max-w-lg rounded-lg ring-2 ring-gray-200 ${
                                    noPadding ? "" : "p-4"
                                }`}>
                                <div
                                    className={`w-4 h-4 absolute -mt-2 bg-white top-0 transform rotate-45 border-t-2 border-l-2 border-gray-200 ${ARROW_POSITION_CLASSES[effectiveArrowPosition]}`}></div>
                                <div className="absolute inset-0 bg-white rounded-lg z-[1]" />
                                <div className="mx-auto max-w-lg z-10 relative text-left">
                                    {typeof children === "function"
                                        ? children({ close })
                                        : children}
                                </div>
                            </div>
                        </PopoverPanel>
                    </>
                );
            }}
        </Popover>
    );
};
